'use client'
import { useCallback, useState } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog/gql'
import {
  AddFavoriteToFavoritesContextMutationVariables,
  FavoritesContextFavoriteFragment,
  RemoveFavoriteFromFavoritesContextMutationVariables,
} from '../../__generated__/graphql/catalog/graphql'
import {
  RSCToastNotifications,
  ToastNotifications,
} from '../_components/dialogs/ToastNotification.client'
import { useNextMutation } from '../_hooks/useNextMutation'
import { useNextQuery } from '../_hooks/useNextQuery'

import { useSession } from './SessionProvider.client'

gql(`
    fragment FavoritesContextFavorite on CustomerProfileFavorite {
      __typename
      id
      productGroupHandle
      color
      fit
      size
    }
`)

const useFavoritesImpl = () => {
  const {
    attributes: { visitorId },
  } = useSession()
  const [favorites, setFavorites] = useState<FavoritesContextFavoriteFragment[]>([])

  const { status } = useNextQuery(
    gql(`
      query GetFavoritesContext($visitorId: ID!, $next: Int!) {
        customerProfile (visitorId: $visitorId)  {
          __typename
          id
          favorites(first: $next, visitorId: $visitorId) {
            edges {
              node {
                ...FavoritesContextFavorite
              }
            }
          }
        }
      }
    `),
    {
      client: 'auth-supported',
      variables: { visitorId, next: 100 }, // Will this hardcoded 100 be an issue?
      onCompleted: data =>
        setFavorites(
          data.customerProfile?.favorites?.edges?.map(edge => edge?.node).filter(node => !!node) ??
            []
        ),
    }
  )

  const [addFavoriteMutation] = useNextMutation(
    gql(`
      mutation AddFavoriteToFavoritesContext($input: CustomerProfileFavoriteInput!, $visitorId: ID) {
        createCustomerProfileFavorite(input: $input, visitorId: $visitorId) {
          ...FavoritesContextFavorite
        }
      }
    `),
    {
      client: 'auth-supported',
    }
  )

  const [removeFavoriteMutation] = useNextMutation(
    gql(`
      mutation RemoveFavoriteFromFavoritesContext($id: ID!, $visitorId: ID) {
        deleteCustomerProfileFavorite(id: $id, visitorId: $visitorId) {
          id
        }
      }
    `),
    {
      client: 'auth-supported',
    }
  )

  const addFavorite = useCallback(
    async ({ input, visitorId }: AddFavoriteToFavoritesContextMutationVariables) => {
      try {
        const { errors, data } = await addFavoriteMutation({
          variables: { input, visitorId },
          optimisticResponse: vars => ({
            createCustomerProfileFavorite: {
              __typename: 'CustomerProfileFavorite',
              id: `${vars.input.productGroupHandle}-${vars.input.color}-${vars.input.fit}-${vars.input.size}`,
              productGroupHandle: input.productGroupHandle,
              fit: input.fit,
              color: input.color,
              size: input.size ?? null,
            },
          }),
          onCompleted: data => {
            setFavorites(f => [...f, data.createCustomerProfileFavorite])
          },
        })

        return { errors, data }
      } catch (error) {
        return { errors: [error], data: null }
      }
    },
    [addFavoriteMutation]
  )

  const removeFavorite = useCallback(
    async (input: RemoveFavoriteFromFavoritesContextMutationVariables) => {
      await removeFavoriteMutation({
        variables: input,
        onCompleted: () => setFavorites(f => f.filter(({ id }) => id !== input.id)),
      })
    },
    [removeFavoriteMutation]
  )

  return {
    favorites,
    addFavorite,
    removeFavorite,
    status,
  }
}
const FavoritesContainer = createContainer(useFavoritesImpl)
export const FavoritesProvider = ({
  children,
  rscSupported = false,
}: {
  children: React.ReactNode
  rscSupported?: boolean
}) => {
  return (
    <FavoritesContainer.Provider>
      {rscSupported ? <RSCToastNotifications /> : <ToastNotifications />}
      {children}
    </FavoritesContainer.Provider>
  )
}

export const useFavorites = FavoritesContainer.useContainer
